/**
 * GeneralDecisionSheet - Post-completion verdict display for General blueprint.
 *
 * Layout:
 * 1. Sticky header bar (question + confidence)
 * 2. TL;DR callout (gold border, serif text, markdown-rendered)
 * 3. "What We Caught" banner (corrections from debate, with task/agent context)
 * 4. Recommended Actions (numbered list)
 * 5. Collapsible: Executive Summary
 * 6. Research Areas (with nested collapse/expand per agent)
 * 7. Debate & Dissent (dedicated full-width cards, not in narrow collapsible)
 * 8. Sources (fully clickable)
 */

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  MessageSquare,
  Shield,
  Bot,
  Link2,
} from 'lucide-react';
import type { ResearchSession, TaskResult, DebateState, SourceInfo } from '@/hooks/useResearchSession';
import { MarkdownContent } from './MarkdownContent';
import { ExportMenu } from './ExportMenu';
import type { ExportFormat } from '@/types/researchModes';
import { getConfidenceDisplay } from '@/utils/confidenceUtils';
import { getSourceBadge, getHostname } from '@/utils/sourceUtils';
import { getProviderFromModel, getProviderColor as getProviderColorUtil } from '@/utils/providerUtils';
import { extractTldr, extractActionItems } from '@/utils/extractDecisionSheetData';

// ============================================================================
// TYPES
// ============================================================================

interface GeneralDecisionSheetProps {
  session: ResearchSession;
  query: string;
  onNewResearch?: () => void;
  onExport?: (format: ExportFormat) => Promise<void>;
  className?: string;
}

// ============================================================================
// INLINE EXPAND/COLLAPSE HELPER
// ============================================================================

function ExpandableMarkdown({ content, previewLines = 4 }: { content: string; previewLines?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const lines = content.split('\n');
  const needsTruncation = lines.length > previewLines;

  const displayContent = isExpanded ? content : lines.slice(0, previewLines).join('\n');

  return (
    <div>
      <div className={cn(!isExpanded && needsTruncation && 'relative')}>
        <MarkdownContent content={displayContent} />
        {!isExpanded && needsTruncation && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#111] to-transparent" />
        )}
      </div>
      {needsTruncation && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-verdict-primary hover:text-verdict-primary/80 text-xs font-medium cursor-pointer"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function GeneralDecisionSheet({
  session,
  query,
  onNewResearch,
  onExport,
  className,
}: GeneralDecisionSheetProps) {
  const reportData = session.reportData;
  if (!reportData) return null;

  const overallConfidence = reportData.confidenceScore ??
    (reportData.taskResults.length > 0
      ? reportData.taskResults.reduce((sum, t) => sum + (t.confidenceScore ?? 0), 0) / reportData.taskResults.length
      : undefined);

  const confidenceDisplay = overallConfidence !== undefined ? getConfidenceDisplay(overallConfidence) : null;

  const debatesPerTask = session.debatesPerTask;
  const hasDebates = debatesPerTask && debatesPerTask.size > 0;

  // Extract TL;DR and action items from verdict
  const tldr = useMemo(() => extractTldr(reportData.verdict), [reportData.verdict]);
  const actionItems = useMemo(() => extractActionItems(reportData.verdict), [reportData.verdict]);

  // Extract corrections from debates with agent context
  const corrections = useMemo(() => {
    if (!hasDebates) return [];
    const items: Array<{ taskIndex: number; taskName: string; discrepancy: string; resolution?: string; agents: string[] }> = [];
    for (const [taskIndex, debate] of debatesPerTask!) {
      if (debate.discrepancies?.length > 0) {
        const taskName = reportData.taskResults[taskIndex]?.taskName || `Task ${taskIndex + 1}`;
        // Collect unique agent names from debate arguments
        const agents = [...new Set(debate.arguments.map(a => a.agentName))];
        for (const disc of debate.discrepancies) {
          items.push({
            taskIndex,
            taskName,
            discrepancy: disc,
            resolution: debate.finalConsensus || undefined,
            agents,
          });
        }
      }
    }
    return items;
  }, [hasDebates, debatesPerTask, reportData.taskResults]);

  // Full summary expand state
  const [showFullSummary, setShowFullSummary] = useState(false);
  const hasFullSummary = reportData.verdict.length > tldr.length + 50;

  // Source counts
  const allSources = reportData.sources;
  const sourceCounts = useMemo(() => {
    const counts = { gov: 0, edu: 0, org: 0 };
    for (const source of allSources) {
      const badge = getSourceBadge(source.url);
      if (badge.type === 'government') counts.gov++;
      else if (badge.type === 'academic') counts.edu++;
      else if (badge.type === 'research') counts.org++;
    }
    return counts;
  }, [allSources]);

  const sourceMetaText = [
    allSources.length > 0 && `${allSources.length} sources`,
    sourceCounts.gov > 0 && `${sourceCounts.gov} government`,
    sourceCounts.edu > 0 && `${sourceCounts.edu} academic`,
  ].filter(Boolean).join(' including ');

  return (
    <div className={cn('w-full', className)}>
      {/* ── STICKY HEADER BAR ── */}
      <div className="sticky top-0 z-10 bg-surface border-b border-border px-5 py-3.5 -mx-1 sm:mx-0 sm:rounded-t-xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-base text-text-primary font-medium truncate">{query}</span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {confidenceDisplay && overallConfidence !== undefined && (
              <span className="inline-flex items-center gap-1.5 bg-verdict-primary/15 text-verdict-primary text-[13px] font-semibold px-3.5 py-1 rounded-full border border-verdict-primary/25">
                {Math.round(overallConfidence * 100)}% {confidenceDisplay.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-[800px] mx-auto px-0 sm:px-5 pb-10">
        {/* ── BOTTOM LINE (merged with executive summary) ── */}
        <div className="mt-6 border-l-4 border-verdict-primary bg-surface/70 backdrop-blur-xl rounded-r-xl px-6 py-5 shadow-[0_4px_20px_rgba(212,165,116,0.15)]">
          <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-muted mb-3">
            Bottom Line
          </div>
          <div className="text-lg leading-relaxed text-text-primary [&_p]:mb-2 [&_strong]:text-text-primary [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
            <MarkdownContent content={showFullSummary ? reportData.verdict : tldr} />
          </div>
          <div className="mt-3.5 flex items-center justify-between">
            {sourceMetaText && (
              <div className="text-xs text-text-muted">
                Based on {sourceMetaText}
              </div>
            )}
            {hasFullSummary && (
              <button
                onClick={() => setShowFullSummary(!showFullSummary)}
                className="text-xs text-verdict-primary hover:text-verdict-primary/80 font-medium cursor-pointer ml-auto"
              >
                {showFullSummary ? 'Show less' : `Full executive summary (~${Math.ceil(reportData.verdict.split(/\s+/).length / 200)} min read)`}
              </button>
            )}
          </div>
        </div>

        {/* ── WHAT WE CAUGHT (Fix 8: includes task name + agents) ── */}
        {corrections.length > 0 && (
          <div className="mt-4 border-l-4 border-teal-500 bg-teal-500/[0.06] rounded-r-xl px-5 py-4">
            <div className="flex items-center gap-2 mb-2.5">
              <Shield className="w-[18px] h-[18px] text-teal-500 flex-shrink-0" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-teal-500">
                Correction Detected
              </span>
            </div>
            {corrections.map((corr, idx) => (
              <div key={idx} className={cn('text-[15px] leading-relaxed', idx > 0 && 'mt-3 pt-3 border-t border-teal-500/20')}>
                <div className="text-[13px] text-teal-500/90 font-medium mb-1">
                  In <span className="italic">{corr.taskName}</span>
                  {corr.agents.length > 0 && (
                    <span className="text-text-muted"> &middot; {corr.agents.join(' vs ')}</span>
                  )}
                </div>
                <div className="text-text-secondary">{corr.discrepancy}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── RECOMMENDED ACTIONS ── */}
        {actionItems.length > 0 && (
          <div className="mt-4 border-t border-b border-border py-5">
            <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-muted mb-4">
              Recommended Actions
            </div>
            <div className="flex flex-col gap-3.5">
              {actionItems.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full border-[1.5px] border-verdict-primary flex items-center justify-center mt-0.5">
                    <span className="text-[13px] font-semibold text-verdict-primary">{index + 1}</span>
                  </div>
                  <div className="text-[15px] text-text-primary leading-relaxed">{item}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── RESEARCH AREAS (Fix 5: nested collapse/expand) ── */}
        {reportData.taskResults.length > 0 && (
          <div className="mt-3">
            {reportData.taskResults.map((task, index) => (
              <div key={index} className={index > 0 ? 'mt-3' : ''}>
                <ResearchAreaCard task={task} index={index} defaultOpen={index === 0} />
              </div>
            ))}
          </div>
        )}

        {/* ── DEBATE & DISSENT (Fix 3: dedicated full-width cards below tasks) ── */}
        {hasDebates && (
          <div className="mt-5">
            <div className="flex items-center gap-2.5 mb-3">
              <MessageSquare className="w-4 h-4 text-teal-500" />
              <span className="text-[15px] font-medium text-text-primary">Debate & Dissent</span>
              {corrections.length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-500">
                  {corrections.length} correction{corrections.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <DebateCards
              debatesPerTask={debatesPerTask!}
              taskResults={reportData.taskResults}
            />
          </div>
        )}

        {/* ── SOURCES (Fix 7: fully clickable) ── */}
        {allSources.length > 0 && (
          <div className="mt-3">
            <CollapsibleSection
              icon={<Link2 className="w-4 h-4 text-text-muted" />}
              title={`Sources (${allSources.length})`}
              badges={[
                sourceCounts.gov > 0 ? { text: `${sourceCounts.gov} GOV`, color: 'green' as const } : null,
                sourceCounts.edu > 0 ? { text: `${sourceCounts.edu} EDU`, color: 'blue' as const } : null,
                sourceCounts.org > 0 ? { text: `${sourceCounts.org} ORG`, color: 'gray' as const } : null,
              ].filter(Boolean) as Array<{ text: string; color: 'green' | 'blue' | 'gray' }>}
            >
              <SourcesList sources={allSources} />
            </CollapsibleSection>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div className="mt-8 flex items-center gap-3">
          {onExport && (
            <ExportMenu onExport={onExport} availableFormats={['pdf', 'markdown']} />
          )}
          {reportData.s3Url && (
            <a
              href={reportData.s3Url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-verdict-primary/20 hover:bg-verdict-primary/30 border border-verdict-primary/40 rounded-lg text-verdict-primary text-sm font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Full Report
            </a>
          )}
          {onNewResearch && (
            <button
              onClick={onNewResearch}
              className="flex items-center gap-2 px-4 py-2 bg-surface hover:bg-surface-light border border-border rounded-lg text-text-secondary text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              New Research
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================

function CollapsibleSection({
  icon,
  title,
  meta,
  badgeText,
  badgeColor,
  badges,
  preview,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  meta?: string;
  badgeText?: string;
  badgeColor?: 'teal' | 'green' | 'blue' | 'amber';
  badges?: Array<{ text: string; color: 'green' | 'blue' | 'gray' }>;
  preview?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const badgeColorMap = {
    teal: 'bg-teal-500/10 text-teal-500',
    green: 'bg-green-500/15 text-green-500',
    blue: 'bg-blue-500/15 text-blue-500',
    amber: 'bg-amber-500/15 text-amber-500',
    gray: 'bg-surface-light text-text-secondary',
  };

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-light transition-colors cursor-pointer select-none"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {icon}
          <span className="text-[15px] font-medium text-text-primary">{title}</span>
          {badgeText && (
            <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-md', badgeColorMap[badgeColor || 'teal'])}>
              {badgeText}
            </span>
          )}
          {badges?.map((badge, i) => (
            <span key={i} className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-md', badgeColorMap[badge.color])}>
              {badge.text}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {meta && <span className="text-xs text-text-muted">{meta}</span>}
          <ChevronDown className={cn(
            'w-[18px] h-[18px] text-text-muted transition-transform duration-300',
            isOpen && 'rotate-180',
          )} />
        </div>
      </button>

      {/* Preview text when collapsed */}
      {!isOpen && preview && (
        <div className="text-[13px] text-text-secondary px-5 pb-3.5 -mt-1.5">
          {preview}
        </div>
      )}

      {/* Content */}
      <div className={cn(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0',
      )}>
        <div className="px-5 pb-5">
          <div className="border-t border-border pt-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RESEARCH AREA CARD (Fix 5: nested collapse/expand)
// ============================================================================

function ResearchAreaCard({
  task,
  index,
  defaultOpen = false,
}: {
  task: TaskResult;
  index: number;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showAgents, setShowAgents] = useState(false);
  const confidenceBadgeClass = task.confidenceScore !== undefined
    ? task.confidenceScore >= 0.75
      ? 'bg-green-500/15 text-green-500'
      : 'bg-amber-500/15 text-amber-500'
    : '';

  // Build preview from aggregated output
  const preview = task.aggregatedOutput
    ? task.aggregatedOutput.replace(/^#+\s.*$/gm, '').replace(/\n+/g, ' ').trim().slice(0, 120) + '...'
    : task.taskDescription || '';

  return (
    <div className={cn('bg-surface border border-border rounded-xl overflow-hidden', isOpen && 'border-border-light')}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-light transition-colors cursor-pointer select-none"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-verdict-primary flex items-center justify-center">
            <span className="text-xs font-semibold text-verdict-primary">{index + 1}</span>
          </div>
          <div className="min-w-0 text-left">
            <div className="text-[15px] font-medium text-text-primary">{task.taskName}</div>
            {!isOpen && preview && (
              <div className="text-sm text-text-secondary truncate mt-0.5 max-w-[500px]">
                {preview}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {task.confidenceScore !== undefined && (
            <span className={cn('text-xs font-semibold px-2.5 py-0.5 rounded-md', confidenceBadgeClass)}>
              {Math.round(task.confidenceScore * 100)}%
            </span>
          )}
          <ChevronDown className={cn(
            'w-[18px] h-[18px] text-text-muted transition-transform duration-300',
            isOpen && 'rotate-180',
          )} />
        </div>
      </button>

      {/* Expanded content */}
      <div className={cn(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0',
      )}>
        <div className="px-5 pb-5">
          <div className="border-t border-border pt-4">
            {/* Consensus output — collapsible for long content */}
            {task.aggregatedOutput && (
              <div className="text-[15px] text-text-secondary leading-relaxed">
                <ExpandableMarkdown content={task.aggregatedOutput} previewLines={6} />
              </div>
            )}

            {/* Per-agent outputs — collapsed by default */}
            {task.agentOutputs.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAgents(!showAgents); }}
                  className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
                >
                  <ChevronDown className={cn(
                    'w-3.5 h-3.5 transition-transform duration-200',
                    showAgents && 'rotate-180',
                  )} />
                  <Bot className="w-3.5 h-3.5" />
                  {task.agentOutputs.length} agent output{task.agentOutputs.length > 1 ? 's' : ''}
                </button>
                <div className={cn(
                  'overflow-hidden transition-all duration-300',
                  showAgents ? 'max-h-[10000px] opacity-100 mt-3' : 'max-h-0 opacity-0',
                )}>
                  <div className="space-y-3">
                    {task.agentOutputs.map((agent, agentIdx) => (
                      <AgentOutputCard key={agentIdx} agent={agent} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Sources */}
            {task.webSources && task.webSources.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] font-medium text-text-muted uppercase tracking-wide mb-1.5">
                  Sources
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {task.webSources.map((src, idx) => (
                    <a
                      key={idx}
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-light border border-border text-[13px] text-verdict-primary hover:bg-verdict-primary/10 hover:border-verdict-primary/30 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {src.title || getHostname(src.url)}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// AGENT OUTPUT CARD (Fix 5: individual agent collapse/expand)
// ============================================================================

function AgentOutputCard({ agent }: { agent: { agentName: string; modelName: string; output: string } }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const provider = getProviderFromModel(agent.modelName);
  const lines = agent.output.split('\n');
  const needsTruncation = lines.length > 3;

  return (
    <div className="border-l-2 border-border-light pl-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 mb-1 cursor-pointer w-full text-left"
      >
        <Bot className="w-3.5 h-3.5" style={{ color: getProviderColorUtil(provider, 'hex') }} />
        <span className="text-xs font-medium" style={{ color: getProviderColorUtil(provider, 'hex') }}>
          {agent.agentName}
        </span>
        <span className="text-xs text-text-muted font-mono">{agent.modelName}</span>
        {needsTruncation && (
          <ChevronDown className={cn(
            'w-3 h-3 text-text-muted transition-transform duration-200 ml-auto',
            isExpanded && 'rotate-180',
          )} />
        )}
      </button>
      {isExpanded ? (
        <MarkdownContent content={agent.output} className="text-[15px] text-text-secondary" />
      ) : (
        <div className="text-[15px] text-text-secondary line-clamp-2">
          <MarkdownContent content={agent.output} className="text-[15px] text-text-secondary" />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DEBATE CARDS (Fix 3: dedicated full-width layout + Fix 6: collapse/expand)
// ============================================================================

function DebateCards({
  debatesPerTask,
  taskResults,
}: {
  debatesPerTask: Map<number, DebateState>;
  taskResults: TaskResult[];
}) {
  const debates = Array.from(debatesPerTask.entries());

  // Summary line
  const totalArgs = debates.reduce((sum, [, d]) => sum + d.arguments.length, 0);
  const avgAgreement = debates.reduce((sum, [, d]) => sum + d.consensusProgress, 0) / debates.length;

  return (
    <div>
      <div className="text-[15px] text-text-secondary leading-relaxed mb-3">
        {debates.length} debate{debates.length > 1 ? 's' : ''} with {totalArgs} total arguments.
        Final agreement at {Math.round(avgAgreement * 100)}%.
      </div>

      <div className="space-y-3">
        {debates.map(([taskIndex, debate]) => (
          <DebateCard
            key={taskIndex}
            taskIndex={taskIndex}
            debate={debate}
            taskName={taskResults[taskIndex]?.taskName || `Task ${taskIndex + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function DebateCard({
  taskIndex,
  debate,
  taskName,
}: {
  taskIndex: number;
  debate: DebateState;
  taskName: string;
}) {
  const [showAllArguments, setShowAllArguments] = useState(false);
  const [expandedConsensus, setExpandedConsensus] = useState(false);
  const isStressTest = debate.debateMode === 'stress_test';

  const visibleArguments = showAllArguments ? debate.arguments : debate.arguments.slice(0, 4);
  const hasMoreArguments = debate.arguments.length > 4;

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden border-l-4 border-l-teal-500/40">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-border/50">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-teal-500 flex-shrink-0" />
          <span className="text-[13px] font-semibold text-text-primary">{taskName}</span>
          <span className="text-[11px] text-text-muted font-mono">Task {taskIndex + 1}</span>
          {debate.consensusProgress > 0 && (
            <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-500">
              {Math.round(debate.consensusProgress * 100)}% agreement
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        {/* Discrepancies / challenges */}
        {debate.discrepancies.map((disc, idx) => (
          <div key={idx} className="text-sm text-text-secondary leading-relaxed border-l-2 border-teal-500/30 pl-3 my-2 italic">
            <div className="text-[11px] text-teal-500/70 font-medium not-italic mb-1">
              {isStressTest ? 'Challenge' : 'Discrepancy'}
            </div>
            {disc}
          </div>
        ))}

        {/* Arguments — show all or first 4 */}
        {visibleArguments.map((arg, idx) => (
          <DebateArgumentItem key={idx} arg={arg} />
        ))}

        {hasMoreArguments && (
          <button
            onClick={() => setShowAllArguments(!showAllArguments)}
            className="mt-2 text-xs text-teal-500 hover:text-teal-400 font-medium cursor-pointer"
          >
            {showAllArguments
              ? 'Show less'
              : `Show all ${debate.arguments.length} arguments`}
          </button>
        )}

        {/* Resolution — expandable */}
        {debate.finalConsensus && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <div className="text-[11px] text-teal-500 font-semibold tracking-wide uppercase mb-1">Resolution</div>
            <div className="text-sm text-teal-400/90 leading-relaxed">
              <MarkdownContent
                content={expandedConsensus || debate.finalConsensus.length <= 200
                  ? debate.finalConsensus
                  : `${debate.finalConsensus.slice(0, 200)}...`}
                className="text-sm text-teal-400/90"
              />
            </div>
            {debate.finalConsensus.length > 200 && (
              <button
                onClick={() => setExpandedConsensus(!expandedConsensus)}
                className="mt-1 text-xs text-teal-500 hover:text-teal-400 font-medium cursor-pointer"
              >
                {expandedConsensus ? 'Show less' : 'Read full resolution'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DebateArgumentItem({ arg }: { arg: { agentName: string; argument: string; roundNumber: number } }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const needsTruncation = arg.argument.length > 150;

  return (
    <div className="text-sm text-text-secondary leading-relaxed border-l-2 border-border-light pl-3 my-2">
      <div className="text-xs text-text-muted font-medium mb-1">
        {arg.agentName} (Round {arg.roundNumber})
      </div>
      {isExpanded || !needsTruncation ? (
        <MarkdownContent content={arg.argument} className="text-sm text-text-secondary" />
      ) : (
        <div className="line-clamp-3">
          <MarkdownContent content={arg.argument} className="text-sm text-text-secondary" />
        </div>
      )}
      {needsTruncation && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-1.5 text-verdict-primary hover:text-verdict-primary/80 text-xs font-medium cursor-pointer"
        >
          {isExpanded ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// SOURCES LIST (Fix 7: fully clickable)
// ============================================================================

function SourcesList({ sources }: { sources: SourceInfo[] }) {
  return (
    <div>
      {sources.map((source, idx) => {
        const badge = getSourceBadge(source.url);
        const badgeColors: Record<string, string> = {
          government: 'bg-green-500/15 text-green-500',
          academic: 'bg-blue-500/15 text-blue-500',
          research: 'bg-green-500/15 text-green-500',
          news: 'bg-surface-light text-text-secondary',
          standard: 'bg-surface-light text-text-secondary',
        };

        return (
          <a
            key={idx}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex items-start gap-3 py-3 group hover:bg-surface-light -mx-2 px-2 rounded-lg transition-colors',
              idx > 0 && 'border-t border-border/50',
            )}
          >
            <span className={cn(
              'flex-shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded mt-0.5 tracking-wide',
              badgeColors[badge.type],
            )}>
              {badge.type === 'government' ? 'GOV' :
               badge.type === 'academic' ? 'EDU' :
               badge.type === 'research' ? 'RES' :
               badge.type === 'news' ? 'NEWS' : 'ORG'}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] text-text-primary font-medium group-hover:text-verdict-primary transition-colors">
                {source.title || getHostname(source.url)}
              </div>
              <div className="text-[13px] text-text-muted font-mono break-all group-hover:text-verdict-primary/70 transition-colors">
                {getHostname(source.url)}
              </div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-text-muted group-hover:text-verdict-primary flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        );
      })}
    </div>
  );
}
